/* eslint-disable prettier/prettier */
/* eslint-disable arrow-parens */
import { useState, useEffect, useContext, useCallback, useRef, Suspense } from "react";
import { Chip } from "@material-ui/core";
import { DynamicForm } from "../../../@types/dynamicForm.types";
import { AuthContext, UIContext } from "../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../constants/caption/dashboardHeader";
import DynamicReportEditableForm from "../../shared/dynamicEditableForm/DynamicEditableForm";
import { api } from "../../../utils/api";
import { Validation } from "../../../@types/validation.types";
import { trackPromise } from "react-promise-tracker";
import SnackbarAPICall from "../../shared/snackbar/SnackbarAPICall";
import { SnackbarProps } from "../../../@types/snackbarAPI.types";
import { Member } from "../../../@types/member.types";
import JSONToExcel from "../../../utils/commonFunctions/JSONToExcel";
import { useEmployerRoute } from "../../../hooks";
import { Group } from "../../../@types/group.types";
import useRouteParam from "../../../hooks/useRouteParam";
import { useLocation } from "react-router-dom";
import { report } from "process";
import DynamicReportEditableFormV2 from "./DynamicReportEditableFormV2";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";

const chipMessage = "This report will pull the active employee details along with their dependents, if needed.";

interface IReportState {
	report_name: string;
	report_type: string;
	report_description: string;
	report_form_structure: any[];
	api_route: string;
}

const DynamicReportForm = (): JSX.Element => {
	const [reportDetailsDynamicForm, setReportDetailsDynamicForm] = useState<DynamicForm>();
	const { setDashboardHeader } = useContext(UIContext);
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");
	const [selectedGroupLocation, setSelectedGroupLocation] = useState<any[]>([]);
	const location = useLocation();
	const { report_name, report_type, report_description, report_form_structure, api_route } =
		(location.state as IReportState) || {};
	// console.log("Location Object==============>", report_form_structure, "report_type", report_type);
	const [selectedGroup, setSelectedGroup] = useState<Group>();
	const [plans, setPlans] = useState<any[]>([]);
	const [reportFieldDetails, setReportFieldDetails] = useState<any>({
		alternative_report_name: "",
		group_number: null,
		with_dependent: false
	});
	const { user } = useContext(AuthContext);
	const [assignedGroups, setAssignedGroups] = useState<any>([]);
	const [validation, setValidation] = useState<Validation>({
		reportFieldDetails: {},
		status: "invalid"
	});
	const _validation = useRef<Validation>();
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const handleValidation = useCallback(async () => {
		_validation.current = {
			...validation,
			reportFieldDetails: {},
			status: "invalid"
		};

		let flag = true;

		if (report_form_structure) {
			report_form_structure.forEach((field: any) => {
				if (field.required) {
					const value = reportFieldDetails[field.name];

					if (
						value === null ||
						value === undefined ||
						value === "" ||
						(field.type === "checkbox" && value === false)
					) {
						_validation.current!.reportFieldDetails[field.name] = `${field.label} is required`;
						flag = false;
					}
				}
			});
		}

		if (flag) {
			_validation.current.status = "valid";
		} else {
			_validation.current.status = "invalid";
			alert("Please fill all required fields to generate the report!");
		}

		setValidation({ ..._validation.current });
		return _validation.current.status;
	}, [reportFieldDetails, validation, report_form_structure]);

	const getAssignGroups = useCallback(async () => {
		const groupField = report_form_structure.find((field) => field.type === "select");

		if (groupField && groupField.options) {
			setAssignedGroups(groupField.options);
		}
	}, [report_form_structure]);

	const getPlans = useCallback(async () => {
		const planField = report_form_structure.filter((field) => field.type === "select");

		if (planField && planField.length > 0) {
			setPlans(planField);
		}
	}, [report_form_structure]);
	// console.log("plans=======>", plans);
	const handleSelectGroup = useCallback(
		(queryGroupNumber: number) => {
			// console.log("Query Group Number", queryGroupNumber);

			assignedGroups.forEach((assignedGroup: any) => {
				if (assignedGroup.group_number === queryGroupNumber) {
					setSelectedGroup(Object.assign({}, assignedGroup));
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							group_number: queryGroupNumber

							// assigned_locations: assignedGroup.assigned_locations || []
						})
					);
				}
			});
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[assignedGroups, reportFieldDetails]
	);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;

			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: value }));
		},
		[reportFieldDetails]
	);

	const handleEnableCheckbox = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, checked } = event.target;
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: checked }));
		},
		[reportFieldDetails]
	);

	const handleGenerateReportSubmit = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validation = await handleValidation();
				if (validation === "valid") {
					const payload: any = {};
					report_form_structure.forEach((field: any) => {
						if (field.name === "alternative_report_name") {
							return;
						}
						let value = reportFieldDetails[field.name];
						if (value === undefined || value === "") {
							value = null;
						}
						if (field.type === "date" && value) {
							value = new Date(value).toISOString().split("T")[0];
						}
						payload[field.name] = value;
					});
					if (report_name === "Benefit Changes Report") {
						payload.type = "Benefit Changes with Termination";
					}

					// console.log(api_route, "Payload============>", payload);

					// const response: Member = await trackPromise(api.reports.censusReport(payload));
					const response: Member = await trackPromise(api.reports.submitApi(api_route, payload));
					if (response) {
						const fileName = reportFieldDetails.alternative_report_name
							? reportFieldDetails.alternative_report_name
							: `${report_name}`;

						// const excelPayload = {
						// 	user: {
						// 		role: user?.role,
						// 		name: user?.first_name + " " + user?.last_name
						// 	},
						// 	reportFieldDetails,
						// 	response
						// };

						const excelPayload = {
							user: {
								role: user?.role,
								name: user?.first_name + " " + user?.last_name
							},
							reportFieldDetails,
							billing_month: payload?.billing_month,
							billing_year: payload?.billing_year,
							api_route,
							response
						};

						await trackPromise(JSONToExcel(excelPayload, fileName));

						setTimeout(() => {
							setSnackbarAPICallProps(
								Object.assign({}, snackbarAPICallProps, {
									open: true,
									message: `${report_name} Fetched Successfully and File Downloaded!`,
									severity: "success"
								})
							);
						}, 2000);
					}
				}
			} catch (error) {
				alert(`${report_name} Generation Unsuccessful!`);
				throw error;
			}
		},
		[handleValidation, reportFieldDetails, user, snackbarAPICallProps, api_route, report_name]
	);

	const handleClearDateField = useCallback((event: React.MouseEvent<HTMLButtonElement>, name: string) => {
		event.stopPropagation();

		setReportFieldDetails((prev: Record<string, string | null>) => ({
			...prev,
			[name]: null
		}));
	}, []);

	useEffect(() => {
		setDashboardHeader(report_type);
		getAssignGroups();
		getPlans();
	}, [getAssignGroups, setDashboardHeader, report_type, getPlans]);

	useEffect(() => {
		if (isEmployerRoute && queryGroupNumber) handleSelectGroup(queryGroupNumber as number);
	}, [isEmployerRoute, queryGroupNumber]);

	useEffect(() => {
		if (!report_name || !report_form_structure) return;

		setReportDetailsDynamicForm(
			Object.assign(
				{},
				{
					[`${report_name} Generation`]: report_form_structure
						.filter((field: any) => {
							if (field.name === "group_number") {
								if (isEmployerRoute && queryGroupNumber) {
									return field.type === "textfield";
								}

								return field.type === "select";
							}
							return true;
						})
						.map((field: any) => {
							const fieldConfig: any = {
								label: field.label,
								name: field.name,
								type: field.type,
								placeholder: field.placeholder,
								required: field.required,
								value: reportFieldDetails[field.name] ?? "",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event)
							};

							if (field.type === "checkbox") {
								fieldConfig.onChange = (event: React.ChangeEvent<HTMLInputElement>) =>
									handleEnableCheckbox(event);
								fieldConfig.value = !!reportFieldDetails[field.name];
							}

							if (field.type === "date") {
								// fieldConfig.value = reportFieldDetails[field.name] ?? "";
								fieldConfig.value =
									reportFieldDetails[field.name] === null
										? null
										: reportFieldDetails[field.name] ?? "";

								fieldConfig.onChange = (eventOrValue: any) => {
									let name = field.name;
									let value = "";

									if (eventOrValue?.target) {
										value = eventOrValue.target.value;
										name = eventOrValue.target.name || field.name;
									} else {
										value = eventOrValue;
									}
									const formattedValue = value ? new Date(value).toISOString().split("T")[0] : "";

									setReportFieldDetails(
										Object.assign({}, reportFieldDetails, { [name]: formattedValue })
									);
								};
							}

							if (field.name === "group_number") {
								// console.log("selectedGroup?.name=====>", selectedGroup?.name);
								fieldConfig.value =
									isEmployerRoute && queryGroupNumber
										? selectedGroup?.name
										: reportFieldDetails.group_number;

								if (field.type === "select") {
									fieldConfig.options = [...assignedGroups];
								}
							}
							if (field.type === "select" && field.name != "group_number") {
								const selectedGroupNumber =
									isEmployerRoute && queryGroupNumber
										? queryGroupNumber
										: reportFieldDetails.group_number;

								// assign correct value based on field
								fieldConfig.value = reportFieldDetails[field.name];

								// dynamically build options from plans
								// console.log("running", plans, selectedGroupNumber, field.name);
								const options = plans.find((plan) => plan.name === field.name);
								// this is old code before implementing filter for carrier_name based on year
								// fieldConfig.options = selectedGroupNumber
								// 	? options?.options[selectedGroupNumber]
								// 		? options?.options[selectedGroupNumber]
								// 		: []
								// 	: [];
								// till here
								let rawOptions =
									selectedGroupNumber && options?.options
										? options.options[selectedGroupNumber] ?? []
										: [];

								//  APPLY FILTER HERE (carrier_name based on billing_year)
								if (field.name === "carrier_name") {
									const year = Number(reportFieldDetails.billing_year);

									// if (year >= 2026) {
									// 	rawOptions = rawOptions.filter((opt: any) => opt.yearType === "NEW");
									// } else {
									// 	rawOptions = rawOptions.filter((opt: any) => opt.yearType === "OLD");
									// }
									rawOptions = rawOptions.filter((opt: any) => {
										if (opt.value === "All") return true; // ✅ keep All always

										if (year >= 2026) {
											return opt.yearType === "NEW";
										} else {
											return opt.yearType === "OLD";
										}
									});
								}

								fieldConfig.options = rawOptions;
								// Alternate Options for the plan selection
								// fieldConfig.options =
								// 	selectedGroupNumber && options?.options
								// 		? options.options[selectedGroupNumber] ?? []
								// 		: [];
							}

							return fieldConfig;
						})
				}
			)
		);
	}, [
		report_name,
		report_form_structure,
		assignedGroups,
		reportFieldDetails,
		handleChange,
		handleEnableCheckbox,
		isEmployerRoute,
		queryGroupNumber,
		selectedGroup?.name
	]);

	// console.log("reportDetailsDynamicForm========>", reportDetailsDynamicForm);
	console.log("handleGenerateReportSubmit=====>", reportFieldDetails);
	// console.log("selectedGroup=====> 22", assignedGroups);
	// console.log("selectedGroup=====>", selectedGroup);

	return (
		<div className="census-report-container" id="census-report-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="chip-container" id="chip-container">
				<Chip className="chip-green" label={report_description} variant="outlined" />
			</div>
			<DynamicReportEditableFormV2
				reportDetailsDynamicForm={reportDetailsDynamicForm}
				// handleClearDateField={() => null}
				handleClearDateField={handleClearDateField}
				validation={validation}
				handleGenerateReportSubmit={handleGenerateReportSubmit}
			/>
		</div>
	);
};

export default DynamicReportForm;
